import { servicePost } from "./client.ts";

/** One product image already in use — `WebListProductImages` (Products.Access.vb). */
export interface ProductImageOption {
  /** Stored file name — what Access_Product.ImageUrl holds and WebSaveProductImageNameAIM takes. */
  ImageName: string;
  /** Operator folder the file lives in (0 = base folder): pictures/f/{OperatorID}/{ImageName}. */
  OperatorID: number;
  /** How many of the caller's products (matching the search) use this image. */
  ProductCount: number;
  /** One of those products, so the picker can say where the image comes from. */
  ExampleProductName: string | null;
}

/** Rows shown before the user searches: the newest few. Each tile loads up to three image URLs,
 *  so a long unfiltered grid is slow — the search is how the rest are reached. */
export const PRODUCT_IMAGE_BROWSE_ROWS = 20;
/** Rows shown when searching. A full page means "refine the search". */
export const PRODUCT_IMAGE_SEARCH_ROWS = 50;
/** Candidates fetched per row shown: many stored names have no file behind them, and only those
 *  whose image loads are shown (util/imagePreview.ts). */
export const PRODUCT_IMAGE_CANDIDATE_FACTOR = 5;
/** The server's own cap on rows per call (ProductImageListMaxRows). */
export const PRODUCT_IMAGE_MAX_CANDIDATES = 500;
const PRODUCT_IMAGE_LIST_ROWS = PRODUCT_IMAGE_SEARCH_ROWS;

/**
 * Existing product images in one operator folder, newest first. `operatorId` is the target
 * product's OperatorID (0 = base folder) — the server only narrows by it, never widens past the
 * caller's operator/ingress scope.
 */
export function listProductImages(operatorId: number, search: string, maxRows = PRODUCT_IMAGE_LIST_ROWS, signal?: AbortSignal) {
  const args: Record<string, unknown> = { operatorId: operatorId > 0 ? operatorId : 0, maxRows };
  const needle = search.trim();
  if (needle) args.search = needle;
  return servicePost<ProductImageOption[]>("WebListProductImages", args, signal);
}

/**
 * Make a picked image the product's main image. A shared image (option.OperatorID 0) picked for an
 * operator's product is copied into that operator's folder server-side; the returned name is the
 * one stored in Access_Product.ImageUrl (it can differ when the folder already had another file
 * with that name).
 */
export function applyProductImage(productId: number, option: Pick<ProductImageOption, "ImageName" | "OperatorID">) {
  return servicePost<string>("WebApplyProductImage", {
    productId,
    imageName: option.ImageName,
    sourceOperatorId: option.OperatorID,
  });
}